import { Controller } from '@hotwired/stimulus'
import Chart from 'chart.js/auto'

export default class extends Controller {
  static targets = ['labChart']

  static values = {
    url: String
  }

  charts = {}

  connect() {
    this.loadData()
  }

  disconnect() {
    Object.values(this.charts).forEach((chart) => chart.destroy())
    this.charts = {}
  }

  async loadData() {
    try {
      const response = await fetch(this.urlValue, {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      this.renderChart(data)
    } catch (error) {
      console.error('Failed to load lab data:', error)
    }
  }

  renderChart(labData) {
    if (!this.hasLabChartTarget || !labData || labData.length === 0) {
      if (this.hasLabChartTarget) {
        this.labChartTarget.innerHTML =
          '<p class="text-sm text-slate-400">No lab data available</p>'
      }
      return
    }

    const canvas = document.createElement('canvas')
    this.labChartTarget.innerHTML = ''
    this.labChartTarget.appendChild(canvas)

    // Sort by total count descending, take top 10
    const sortedData = labData.sort((a, b) => b.total_count - a.total_count).slice(0, 10)

    const labels = sortedData.map((d) => this.truncateLabel(d.lab_name))
    const successCounts = sortedData.map((d) => d.success_count)
    const failedCounts = sortedData.map((d) => d.failed_count)

    this.charts.lab = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Successful',
            data: successCounts,
            backgroundColor: '#10B981',
            borderWidth: 0,
            borderRadius: 2
          },
          {
            label: 'Failed',
            data: failedCounts,
            backgroundColor: '#EF4444',
            borderWidth: 0,
            borderRadius: 2
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              boxWidth: 12,
              padding: 8,
              font: { size: 10 }
            }
          },
          tooltip: {
            callbacks: {
              afterLabel: (context) => {
                const lab = sortedData[context.dataIndex]
                const total = lab.success_count + lab.failed_count
                const rate = total > 0 ? ((lab.success_count / total) * 100).toFixed(1) : 0
                return `Success rate: ${rate}%`
              }
            }
          }
        },
        scales: {
          x: {
            stacked: true,
            beginAtZero: true,
            grid: { color: '#E2E8F0' },
            ticks: { font: { size: 10 } }
          },
          y: {
            stacked: true,
            grid: { display: false },
            ticks: { font: { size: 10 } }
          }
        }
      }
    })
  }

  truncateLabel(label) {
    if (!label) return 'Unknown'
    return label.length > 20 ? `${label.substring(0, 17)}...` : label
  }
}
